import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Box,
  Chip,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Email as EmailIcon,
  Send as SendIcon,
  History as HistoryIcon,
  Visibility as VisibilityIcon,
  Assessment as AssessmentIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Settings as SettingsIcon
} from '@mui/icons-material';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.paceestate.com';

export default function EmailTemplates() {
  const [templates, setTemplates] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [stats, setStats] = useState(null);
  const [smtpConfig, setSmtpConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentTab, setCurrentTab] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalNotifications, setTotalNotifications] = useState(0);

  // Dialog states
  const [testDialogOpen, setTestDialogOpen] = useState(false);
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  // Form states
  const [testEmail, setTestEmail] = useState('');
  const [sendForm, setSendForm] = useState({
    recipient_email: '',
    recipient_name: '',
    subject: '',
    template_name: ''
  });

  // Notification states
  const [alert, setAlert] = useState({ show: false, message: '', severity: 'info' });

  useEffect(() => {
    loadData();
  }, [currentTab, page, rowsPerPage]);

  const loadData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      // Load templates
      const templatesRes = await axios.get(`${API_URL}/api/notifications/templates`, { headers });
      setTemplates(templatesRes.data.templates);

      // Load SMTP config
      const configRes = await axios.get(`${API_URL}/api/notifications/config`, { headers });
      setSmtpConfig(configRes.data);

      // Load stats
      const statsRes = await axios.get(`${API_URL}/api/notifications/stats?days=30`, { headers });
      setStats(statsRes.data);

      // Load notification history if on that tab
      if (currentTab === 1) {
        const historyRes = await axios.get(
          `${API_URL}/api/notifications/history?page=${page + 1}&per_page=${rowsPerPage}`,
          { headers }
        );
        setNotifications(historyRes.data.notifications);
        setTotalNotifications(historyRes.data.total);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      setAlert({ show: true, message: 'Failed to load email data', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleSendTestEmail = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/api/notifications/test`,
        { recipient_email: testEmail },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setAlert({ show: true, message: 'Test email sent successfully!', severity: 'success' });
      setTestDialogOpen(false);
      setTestEmail('');
      loadData();
    } catch (error) {
      setAlert({ show: true, message: 'Failed to send test email', severity: 'error' });
    }
  };

  const handleSendManualEmail = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/api/notifications/send`,
        sendForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setAlert({ show: true, message: 'Email sent successfully!', severity: 'success' });
      setSendDialogOpen(false);
      setSendForm({ recipient_email: '', recipient_name: '', subject: '', template_name: '' });
      loadData();
    } catch (error) {
      setAlert({ show: true, message: 'Failed to send email', severity: 'error' });
    }
  };

  const handleViewTemplate = async (templateName) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/api/notifications/templates/${templateName}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSelectedTemplate(response.data);
      setPreviewDialogOpen(true);
    } catch (error) {
      setAlert({ show: true, message: 'Failed to load template', severity: 'error' });
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'sent':
        return <CheckCircleIcon color="success" />;
      case 'failed':
        return <ErrorIcon color="error" />;
      default:
        return <CircularProgress size={20} />;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {alert.show && (
        <Alert severity={alert.severity} onClose={() => setAlert({ ...alert, show: false })} sx={{ mb: 2 }}>
          {alert.message}
        </Alert>
      )}

      <Typography variant="h4" gutterBottom>
        <EmailIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Email Notification System
      </Typography>

      {/* SMTP Configuration Status */}
      {smtpConfig && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography variant="h6">
                <SettingsIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                SMTP Configuration
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Server: {smtpConfig.smtp_server}:{smtpConfig.smtp_port}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                From: {smtpConfig.smtp_from_name} &lt;{smtpConfig.smtp_from_email}&gt;
              </Typography>
            </Grid>
            <Grid item xs={12} md={6} textAlign={{ md: 'right' }}>
              <Chip
                label={smtpConfig.configured ? 'Configured' : 'Not Configured'}
                color={smtpConfig.configured ? 'success' : 'error'}
                sx={{ mr: 1 }}
              />
              <Button
                variant="outlined"
                startIcon={<SendIcon />}
                onClick={() => setTestDialogOpen(true)}
                disabled={!smtpConfig.configured}
              >
                Send Test Email
              </Button>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Statistics */}
      {stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>Total Sent (30 days)</Typography>
                <Typography variant="h4">{stats.total}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>Successfully Sent</Typography>
                <Typography variant="h4" color="success.main">{stats.sent}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>Failed</Typography>
                <Typography variant="h4" color="error.main">{stats.failed}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>Success Rate</Typography>
                <Typography variant="h4">{stats.success_rate}%</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Tabs */}
      <Paper sx={{ mb: 2 }}>
        <Tabs value={currentTab} onChange={(e, newValue) => setCurrentTab(newValue)}>
          <Tab icon={<EmailIcon />} label="Email Templates" />
          <Tab icon={<HistoryIcon />} label="Notification History" />
        </Tabs>
      </Paper>

      {/* Templates Tab */}
      {currentTab === 0 && (
        <Grid container spacing={3}>
          {loading ? (
            <Grid item xs={12} textAlign="center">
              <CircularProgress />
            </Grid>
          ) : (
            templates.map((template) => (
              <Grid item xs={12} md={6} lg={4} key={template.name}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      {template.name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {template.description}
                    </Typography>
                    <Chip
                      label={template.filename}
                      size="small"
                      sx={{ mt: 1 }}
                      variant="outlined"
                    />
                  </CardContent>
                  <CardActions>
                    <Button
                      size="small"
                      startIcon={<VisibilityIcon />}
                      onClick={() => handleViewTemplate(template.name)}
                    >
                      View
                    </Button>
                    <Button
                      size="small"
                      startIcon={<SendIcon />}
                      onClick={() => {
                        setSendForm({ ...sendForm, template_name: template.name });
                        setSendDialogOpen(true);
                      }}
                    >
                      Send
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))
          )}
        </Grid>
      )}

      {/* History Tab */}
      {currentTab === 1 && (
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Status</TableCell>
                  <TableCell>Recipient</TableCell>
                  <TableCell>Subject</TableCell>
                  <TableCell>Template</TableCell>
                  <TableCell>Sent At</TableCell>
                  <TableCell>Type</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : notifications.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      No notifications found
                    </TableCell>
                  </TableRow>
                ) : (
                  notifications.map((notification) => (
                    <TableRow key={notification.id}>
                      <TableCell>
                        <Tooltip title={notification.status}>
                          {getStatusIcon(notification.status)}
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{notification.recipient_email}</Typography>
                        {notification.recipient_name && (
                          <Typography variant="caption" color="text.secondary">
                            {notification.recipient_name}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>{notification.subject}</TableCell>
                      <TableCell>
                        <Chip label={notification.template_name} size="small" />
                      </TableCell>
                      <TableCell>{formatDate(notification.sent_at || notification.created_at)}</TableCell>
                      <TableCell>
                        {notification.entity_type && (
                          <Chip label={notification.entity_type} size="small" variant="outlined" />
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={totalNotifications}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
          />
        </Paper>
      )}

      {/* Test Email Dialog */}
      <Dialog open={testDialogOpen} onClose={() => setTestDialogOpen(false)}>
        <DialogTitle>Send Test Email</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Recipient Email"
            type="email"
            fullWidth
            value={testEmail}
            onChange={(e) => setTestEmail(e.target.value)}
            sx={{ mt: 1 }}
          />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            This will send a test email to verify your SMTP configuration is working correctly.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTestDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSendTestEmail}
            variant="contained"
            disabled={!testEmail}
          >
            Send Test
          </Button>
        </DialogActions>
      </Dialog>

      {/* Send Manual Email Dialog */}
      <Dialog open={sendDialogOpen} onClose={() => setSendDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Send Email</DialogTitle>
        <DialogContent>
          <TextField
            margin="dense"
            label="Recipient Email"
            type="email"
            fullWidth
            value={sendForm.recipient_email}
            onChange={(e) => setSendForm({ ...sendForm, recipient_email: e.target.value })}
            sx={{ mt: 1 }}
          />
          <TextField
            margin="dense"
            label="Recipient Name (Optional)"
            fullWidth
            value={sendForm.recipient_name}
            onChange={(e) => setSendForm({ ...sendForm, recipient_name: e.target.value })}
          />
          <TextField
            margin="dense"
            label="Subject"
            fullWidth
            value={sendForm.subject}
            onChange={(e) => setSendForm({ ...sendForm, subject: e.target.value })}
          />
          <TextField
            margin="dense"
            label="Template"
            fullWidth
            value={sendForm.template_name}
            InputProps={{ readOnly: true }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSendDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSendManualEmail}
            variant="contained"
            disabled={!sendForm.recipient_email || !sendForm.subject || !sendForm.template_name}
          >
            Send Email
          </Button>
        </DialogActions>
      </Dialog>

      {/* Template Preview Dialog */}
      <Dialog open={previewDialogOpen} onClose={() => setPreviewDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Template Preview</DialogTitle>
        <DialogContent>
          {selectedTemplate && (
            <Box>
              <Typography variant="h6" gutterBottom>
                {selectedTemplate.name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
              </Typography>
              <Box sx={{ border: 1, borderColor: 'divider', p: 2, borderRadius: 1, bgcolor: 'grey.50', maxHeight: 500, overflow: 'auto' }}>
                <pre style={{ whiteSpace: 'pre-wrap', fontSize: '12px' }}>
                  {selectedTemplate.content}
                </pre>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
